import { useQuery } from '@tanstack/react-query'
import { snmpDashboardQuery } from '../api/proactivitee'
import { moduleOf, shellQuery } from '../api/shell'
import { DashboardCardSlot } from '../dashboard/DashboardCard'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * SNMP Dashboard, pe desenul de acum al tablourilor de bord.
 *
 * -----------------------------------------------------------------------------
 * ACELASI CARD CA PE CELELALTE SASE TABLOURI
 * -----------------------------------------------------------------------------
 *
 * Pagina nu deseneaza niciun card: le primeste gata scrise din API
 * (`App\Proactivitee\Snmp\SnmpBoard` -> `CardMarkup`, adica exact markupul pe
 * care il foloseste si tabloul central, si wallboardul, si cele cinci tablouri
 * de modul) si le pune in grila `.odb` din `od_dashboard.css`.
 *
 * Ce era pana acum: sase dale `dashboard-stat` - cartonasul colorat pe toata
 * suprafata, cu text alb peste, de care foaia noua spune in capul ei de ce ne-am
 * despartit - si trei panouri `panel panel-danger` cu grafice. Panourile alea
 * erau reconstructia unor grafice amCharts pe care portalul le stersese deja (6
 * septembrie 2026, auditul bancar), iar al treilea citea nume de camp care nu
 * exista in raspuns: fiecare masina iesea cu nume gol si bara de zero.
 *
 * -----------------------------------------------------------------------------
 * CARDUL E COPILUL DIRECT AL LOCULUI
 * -----------------------------------------------------------------------------
 *
 * `DashboardCardSlot` se ocupa de asta si scrie de ce: `.odb-slot > .odb-card`
 * e regula care tine cardul in celula, iar un `<div>` pus intre ele o face sa nu
 * se mai potriveasca - cardurile cresc peste celula si se aseaza unul peste
 * altul. Pagina asta NU isi scrie propriul loc, ca sa nu mai existe inca un loc
 * in care greseala aia se poate repeta.
 *
 * Fara banda de pastile: ecranul n-are perspective, clase de active sau
 * ferestre de timp - vezi `SnmpBoard`.
 */
export function SnmpDashboardPage() {
  const page = useQuery(snmpDashboardQuery())
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  if (page.isPending || page.isError || !page.data) return <div className="odb-page" />

  const board = page.data
  const unreadable = board.tally?.unreadable ?? 0
  const read = board.tally?.read ?? 0

  return (
    <div className="odb-page">
      <div className="odb-bar-top">
        <p className="odb-lead">
          {board.lead}{' '}
          {board.noCompany && (
            <span className="odb-lead-warn">
              No company is selected, so there is nothing to count. Pick one from the top bar.
            </span>
          )}
          {!board.noCompany && !board.read && (
            <span className="odb-lead-warn">
              The monitoring tables could not be read just now, so these cards show a question mark - that is not the same
              as zero.
            </span>
          )}
          {!board.noCompany && board.read && unreadable > 0 && (
            <span className="odb-lead-warn">
              {unreadable} of {read + unreadable} cards could not be read - that is not the same as zero.
            </span>
          )}
        </p>
      </div>
      {board.cards.length === 0 ? (
        <div className="odb-empty">
          <i className="fas fa-lock" aria-hidden="true"></i> Nothing here is open to you. Ask an administrator for the
          rights.
        </div>
      ) : (
        <div className="odb" id="odbGrid">
          {board.cards.map((card) => (
            <DashboardCardSlot card={card} edit={false} sizes={{}} key={card.id} />
          ))}
        </div>
      )}
    </div>
  )
}
